import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { AuthContext, type AuthContextValue } from './auth-context';
import {
  accessToken,
  adopt,
  clear,
  currentIdentity,
  restore,
  subscribe,
  type Identity,
} from './session';
import * as turnstile from './turnstile-api';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(currentIdentity);
  const [restoring, setRestoring] = useState(true);

  // The session module is the single source of truth — Apollo's auth link
  // refreshes and clears it without going through React — so the provider
  // subscribes rather than owning the value.
  useEffect(() => subscribe(setIdentity), []);

  useEffect(() => {
    let cancelled = false;
    restore()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setRestoring(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const requestPin = useCallback(async (email: string) => {
    const { challenge_id } = await turnstile.challenge(email);
    return challenge_id;
  }, []);

  const submitPin = useCallback(async (challengeId: string, pin: string) => {
    adopt(await turnstile.verify(challengeId, pin));
  }, []);

  const signOut = useCallback(async () => {
    const token = currentIdentity() ? await accessToken().catch(() => null) : null;
    if (token) {
      // A failed revoke must not strand the user in a signed-in shell they
      // can no longer use; the local session is dropped either way.
      await turnstile.logout(token).catch(() => undefined);
    }
    clear();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: restoring ? 'restoring' : identity ? 'authenticated' : 'anonymous',
      identity,
      requestPin,
      submitPin,
      signOut,
    }),
    [restoring, identity, requestPin, submitPin, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
